// Painted Mediterranean sky for WORLD COSTA BLANCA.
//
// SOURCE: donor technique from sakura-crossing src/core/sky.js (REPURPOSE —
//   gradient dome + billboard clouds + silhouette ridge layers; palette and
//   mood re-authored as Sorolla coastal light, not anime sky).
// ADAPTATION (2026-08-19, Kimi K3): warm peach haze at the horizon rising to
//   azure; ridge silhouettes read as Sierra de Bernia in coastal haze.
//   Everything fits the runtime camera (far plane 160).

import * as THREE from 'three';
import { flat } from './toon.js';
import { cloudTexture } from '../builders/textures.js';
import { createRng } from '../runtime/rng.js';

// Sorolla coastal sky: peach haze low, pale cyan mid, azure top.
export const SKY = {
  top: 0x7cb5e6,
  mid: 0xcfe6f0,
  haze: 0xfde9d2,
  cloud: 0xfffdf8,
  cloudShade: 0xdfe4f0,
  ridgeNear: 0xa8b2cc,
  ridgeFar: 0xc3c9dc
};

/** Three-stop painted gradient dome. Slight banding is intentional — it
 *  reads as airbrushed background art rather than a physical sky. */
export function buildSky(scene, radius = 132) {
  const geo = new THREE.SphereGeometry(radius, 32, 20);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: true,
    fog: false,
    uniforms: {
      uTop: { value: new THREE.Color(SKY.top) },
      uMid: { value: new THREE.Color(SKY.mid) },
      uHaze: { value: new THREE.Color(SKY.haze) },
      uBands: { value: 26.0 }
    },
    vertexShader: /* glsl */ `
      varying vec3 vWorld;
      void main() {
        vec4 wp = modelMatrix * vec4( position, 1.0 );
        vWorld = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uTop, uMid, uHaze;
      uniform float uBands;
      varying vec3 vWorld;
      void main() {
        float h = normalize( vWorld ).y;
        float t = clamp( h * 1.15 + 0.02, 0.0, 1.0 );
        float q = floor( t * uBands ) / uBands;
        t = mix( t, q, 0.35 ); // soft quantisation: mostly smooth, faint painted step
        vec3 col = mix( uHaze, uMid, smoothstep( 0.0, 0.30, t ) );
        col = mix( col, uTop, smoothstep( 0.26, 0.92, t ) );
        // warmth only in a low band ABOVE the horizon; below it the dome cools
        // to a quiet sea-haze so the world edge never reads as a peach wall
        col = mix( col, uHaze, smoothstep( 0.14, 0.02, h ) * step( 0.0, h ) * 0.55 );
        vec3 below = mix( uMid, uHaze, 0.30 );
        col = mix( below, col, smoothstep( -0.03, 0.015, h ) );
        gl_FragColor = vec4( col, 1.0 );
      }
    `
  });
  const dome = new THREE.Mesh(geo, mat);
  dome.frustumCulled = false;
  dome.renderOrder = -10;
  scene.add(dome);

  // --- flat cel clouds: one ring of billboarded puffs, no depth writes ---
  const tex = cloudTexture();
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  const rng = createRng(7781);
  const clouds = new THREE.Group();
  const matA = flat({ color: SKY.cloud, map: tex, transparent: true, opacity: .6, depthWrite: false, fog: false, cache: false });
  const matB = flat({ color: SKY.cloudShade, map: tex, transparent: true, opacity: .32, depthWrite: false, fog: false, cache: false });
  for (let i = 0; i < 14; i += 1) {
    const r = 62 + rng() * 46;
    const a = rng() * Math.PI * 2;
    const w = 26 + rng() * 34;
    const h = w * (.24 + rng() * .1);
    const y = 24 + rng() * 34;
    const g = new THREE.Group();
    const back = new THREE.Mesh(new THREE.PlaneGeometry(w, h), matB);
    back.position.set(.6, -h * .1, -.5);
    const front = new THREE.Mesh(new THREE.PlaneGeometry(w, h), matA);
    g.add(back, front);
    g.position.set(Math.cos(a) * r, y, Math.sin(a) * r);
    g.lookAt(0, y * .55, 0);
    g.renderOrder = -9;
    clouds.add(g);
  }
  clouds.frustumCulled = false;
  scene.add(clouds);

  return { dome, clouds };
}

/** Layered blue-mauve ridge silhouettes (Sierra de Bernia in coastal haze).
 *  Unlit painted flats, mirrored behind the camera so orbiting 360° still
 *  reads as a valley enclosed by sierra. */
export function buildRidgeLines(scene) {
  const group = new THREE.Group();
  const rng = createRng(4242);
  const layers = [
    { dist: 130, h: 22, color: SKY.ridgeFar, width: 700, bumps: 9, y: -2 },
    { dist: 112, h: 15, color: SKY.ridgeNear, width: 600, bumps: 7, y: -1 }
  ];
  for (const L of layers) {
    const pts = [];
    const n = 90;
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const x = (t - .5) * L.width;
      let y = 0;
      for (let b = 1; b <= L.bumps; b += 1) y += Math.sin(t * Math.PI * b * 1.7 + b * 2.1) * (L.h / (b * 1.25));
      pts.push(new THREE.Vector2(x, Math.max(1.2, y * .55 + L.h * .55)));
    }
    const shape = new THREE.Shape();
    shape.moveTo(pts[0].x, -30);
    pts.forEach((p) => shape.lineTo(p.x, p.y));
    shape.lineTo(pts[pts.length - 1].x, -30);
    shape.closePath();
    const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), flat({ color: L.color, fog: false }));
    mesh.position.set((rng() - .5) * 20, L.y, -L.dist);
    mesh.renderOrder = -8;
    group.add(mesh);
    // mirrored copy behind so turning around still reads as enclosed valley
    const back = mesh.clone();
    back.position.set((rng() - .5) * 24, L.y, L.dist * 1.1);
    back.rotation.y = Math.PI;
    group.add(back);
  }
  scene.add(group);
  return group;
}
